<script>
import Layout from "../../layouts/main.vue";
import PageHeader from "@/components/page-header";
import Tab from "@/components/ar/Tab";
import Stat from "@/components/ar/stat";
import appConfig from "../../../app.config";
import axios from 'axios';
import { submitBulk } from '@/helpers/api/mdm/mdm-command-logs.api';
import { commonService } from '@/helpers/common.service';
import { usePermissions } from '@/composables/usePermissions';

export default {
    page: {
        title: "MDM Enrolled Device",
        meta: [{ name: "description", content: appConfig.description }],
    },
    components: {
        Layout,
        PageHeader,
        Tab,
        Stat,
    },
    setup() {
        const { canRequestMdm } = usePermissions();
        return { canRequestMdm };
    },
    data() {
        return {
            title: "Customer Information",
            items: [
                { text: "Customer", href: "/" },
                { text: "Customer Information", href: "/customer/customer-details/" + this.$route.params.id },
                { text: "AR", active: true },
            ],
            nameperic: '---',
            order_id: '',
            
            // API Data
            loading: true,
            deviceDetails: null,
            contractNumber: null,

            // Modal State
            showModal: false,
            modalAction: '',
            selectedTemplateId: null,
            templatesLoading: false,
            templates: [],
            isSubmitting: false
        };
    },
    computed: {
        activeTemplate() {
            return this.templates.find(t => t.id === this.selectedTemplateId) || null;
        },
        modalTitle() {
            switch(this.modalAction) {
                case 1: return 'Send Warning';
                case 2: return 'Enable Lost Mode';
                case 3: return 'Disable Lost Mode';
                default: return 'MDM Action';
            }
        }
    },
    methods: {
        async get_customer_by_id() {
            try {
                axios.defaults.headers.common['authorization'] = 'Bearer ' + localStorage.getItem('jwt');
                const response = await axios.get(process.env.VUE_APP_AR_API_BASEURL + 'customer/' + this.$route.params.id);
                if (response.data) {
                    this.nameperic = response.data.name_as_per_nric;
                    this.order_id = response.data.order?.orderId || '';
                }
            } catch (error) {
                console.error("Error fetching customer:", error);
            }
        },

        async fetchMdmDetails() {
            this.loading = true;
            try {
                axios.defaults.headers.common['authorization'] = 'Bearer ' + localStorage.getItem('jwt');
                const params = {
                    customer_id: this.$route.params.id,
                    paginate: 0
                };
                const response = await axios.get(process.env.VUE_APP_API_BASEURL + 'mdm', { params });
                
                if (response.data && response.data.data && response.data.data.length > 0) {
                    const item = response.data.data[0];
                    this.deviceDetails = {
                        name: item.device_details?.product_name || '---',
                        sn: item.device_details?.serial_number || '---',
                        imei1: item.device_details?.imei_1 || '---',
                        imei2: item.device_details?.imei_2 || '---',
                        status: item.mdm_details?.status_description || 'Unknown',
                        statusId: item.mdm_details?.status || 0,
                        enrollment: item.mdm_details?.mdm_profile_status || 'unknown',
                        enrolledDate: item.mdm_details?.enrolled_at || '---',
                        history: {
                            warn: item.command_statistics?.warning_count || 0,
                            lost: item.command_statistics?.lost_mode_enabled_count || 0,
                            disable: item.command_statistics?.lost_mode_disabled_count || 0
                        }
                    };
                    this.contractNumber = item.contract?.number;
                } else {
                    this.deviceDetails = null;
                }
            } catch (error) {
                console.error("Error fetching MDM details:", error);
                commonService.swalPrompt(false, "Failed to load MDM device details.", "Error");
            } finally {
                this.loading = false;
            }
        },

        async fetchTemplates(type) {
            this.templatesLoading = true;
            try {
                axios.defaults.headers.common['authorization'] = 'Bearer ' + localStorage.getItem('jwt');
                const response = await axios.get(process.env.VUE_APP_API_BASEURL + 'mdm/templates', {
                    params: { type, status: 1 }
                });
                this.templates = response.data?.data || (Array.isArray(response.data) ? response.data : []);
                if (this.templates.length > 0) {
                    this.selectedTemplateId = this.templates[0].id;
                }
            } catch (error) {
                console.error("Error fetching templates:", error);
            } finally {
                this.templatesLoading = false;
            }
        },

        openRequestModal(actionType) {
            this.modalAction = actionType;
            this.selectedTemplateId = null;
            if (actionType !== 3) {
                this.fetchTemplates(actionType);
            }
            this.showModal = true;
        },

        async requestApproval() {
            if (this.modalAction !== 3 && !this.selectedTemplateId) return;
            if (!this.contractNumber) return;

            const res = await commonService.swalConfirm(
                `Request ${this.modalTitle} Approval?`,
                `Are you sure you want to submit this request for approval?`
            );
            if (!res) return;

            this.isSubmitting = true;
            try {
                const response = await submitBulk([this.contractNumber], this.modalAction, this.selectedTemplateId);
                commonService.swalPrompt(true, response.message || "Approval request has been sent successfully.", "Success");
                this.showModal = false;
            } catch (error) {
                const msg = error.response?.data?.message || "Failed to submit approval request.";
                commonService.swalPrompt(false, msg, "Submission Failed");
            } finally {
                this.isSubmitting = false;
            }
        }
    },
    mounted() {
        this.get_customer_by_id();
        this.fetchMdmDetails();
    }
};
</script>

<template>
    <Layout>
        <PageHeader :title="title" :items="items" />
        <div class="row">
            <div class="col-lg-12">
                <div class="card">
                    <div class="card-header border-0">
                        <div class="d-flex align-items-center">
                            <h3><i class="ri-function-fill text-muted align-bottom me-1"></i>{{ nameperic }}</h3>
                        </div>
                    </div>
                    <div>
                        <ul class="nav nav-tabs nav-tabs-custom" role="tablist">
                            <li class="nav-item">
                                <router-link :to="'/customer/customer-details/'+this.$route.params.id" class="nav-link">
                                    <i class="ri-information-line text-muted align-bottom me-1"></i> Summary
                                </router-link>
                            </li>
                            <li class="nav-item">
                                <router-link :to="'/customer/customer-details/'+this.$route.params.id" class="nav-link">
                                    <i class="ri-shopping-cart-2-line text-muted align-bottom me-1"></i> Orders
                                </router-link>
                            </li>
                            <li class="nav-item">
                                <router-link :to="'/customer/customer-details/'+this.$route.params.id" class="nav-link">
                                    <i class="ri-file-edit-line text-muted align-bottom me-1"></i> Contracts
                                </router-link>
                            </li>
                            <li class="nav-item">
                                <router-link :to="'/customer/customer-details/'+this.$route.params.id" class="nav-link">
                                    <i class="ri-checkbox-multiple-line text-muted align-bottom me-1"></i> Customer Checks
                                </router-link>
                            </li>
                            <li class="nav-item">
                                <router-link :to="'/customer/customer-details/'+this.$route.params.id+'/ar'" class="nav-link active">
                                    <i class="ri-checkbox-multiple-line text-muted align-bottom me-1"></i> AR
                                </router-link>
                            </li>
                        </ul>
                    </div>
                    <div class="row project-wrapper">
                        <div class="col-xxl-12 mb-5">
                            <Stat :cust_id="$route.params.id" :order_id="order_id" />
                        </div>
                    </div>

                    <div class="row">
                        <div class="col-lg-12">
                            <div class="card">
                                <div>
                                    <ul class="nav nav-tabs nav-tabs-custom" role="tablist">
                                        <Tab typr="mdmenrolled" />
                                    </ul>
                                </div>
                                <div class="card-body p-4">
                                    <div v-if="loading" class="text-center py-5">
                                        <div class="spinner-border text-primary" role="status">
                                            <span class="sr-only">Loading...</span>
                                        </div>
                                        <p class="mt-2 text-muted">Fetching MDM device details...</p>
                                    </div>

                                    <div v-else-if="!deviceDetails" class="text-center py-5">
                                        <div class="avatar-sm mx-auto mb-3">
                                            <div class="avatar-title bg-soft-info text-info fs-24 rounded-circle">
                                                <i class="ri-device-line"></i>
                                            </div>
                                        </div>
                                        <h5 class="fw-bold">No Enrolled device found</h5>
                                        <p class="text-muted">This customer does not have any MDM-enrolled devices associated with their account.</p>
                                    </div>

                                    <div v-else class="row">
                                        <div class="col-md-6">
                                            <h5 class="mb-3">MDM Device Details</h5>
                                            <div class="table-responsive border rounded">
                                                <table class="table table-bordered table-nowrap align-middle mb-0">
                                                    <tbody>
                                                        <tr>
                                                            <th class="bg-light" style="width: 200px;">Device Name</th>
                                                            <td>{{ deviceDetails.name }}</td>
                                                        </tr>
                                                        <tr>
                                                            <th class="bg-light">Serial Number</th>
                                                            <td>{{ deviceDetails.sn }}</td>
                                                        </tr>
                                                        <tr>
                                                            <th class="bg-light">IMEI 1</th>
                                                            <td>{{ deviceDetails.imei1 }}</td>
                                                        </tr>
                                                        <tr>
                                                            <th class="bg-light">IMEI 2</th>
                                                            <td>{{ deviceDetails.imei2 }}</td>
                                                        </tr>
                                                        <tr>
                                                            <th class="bg-light">MDM Status</th>
                                                            <td>
                                                                <span class="badge badge-soft-success me-1">{{ deviceDetails.status }}</span>
                                                                <span class="badge badge-soft-info text-uppercase">{{ deviceDetails.enrollment }}</span>
                                                            </td>
                                                        </tr>
                                                        <tr>
                                                            <th class="bg-light">Enrolled Date</th>
                                                            <td>{{ deviceDetails.enrolledDate }}</td>
                                                        </tr>
                                                        <tr>
                                                            <th class="bg-light">History</th>
                                                            <td>
                                                                <span class="text-warning fw-bold">Warn: {{ deviceDetails.history.warn }}</span> | 
                                                                <span class="text-danger fw-bold">Lost: {{ deviceDetails.history.lost }}</span> | 
                                                                <span class="text-info fw-bold">Disable: {{ deviceDetails.history.disable }}</span>
                                                            </td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                        <div class="col-md-6 d-flex flex-column justify-content-center align-items-center" v-if="canRequestMdm">
                                            <div class="w-75 mb-3">
                                                <div v-if="deviceDetails.statusId !== 3" class="alert alert-info border-dashed p-2 fs-12 mb-0">
                                                    <i class="ri-information-line me-1 align-bottom"></i> Device is in <strong>{{ deviceDetails.status }}</strong> status. Management actions are enabled.
                                                </div>
                                                <div v-else class="alert alert-danger border-dashed p-2 fs-12 mb-0">
                                                    <i class="ri-error-warning-line me-1 align-bottom"></i> Device is in <strong>Lost Mode</strong> status. Only unlock action is available.
                                                </div>
                                            </div>

                                            <div class="d-grid gap-2 w-75">
                                                <!-- If status is NOT LOST_MODE (3), show Warning and Enable Lost Mode -->
                                                <template v-if="deviceDetails.statusId !== 3">
                                                    <button class="btn btn-warning waves-effect waves-light py-2 fs-13 shadow-sm" @click="openRequestModal(1)">
                                                        <i class="ri-error-warning-line me-2 align-bottom"></i> Send Warning
                                                    </button>
                                                    <button class="btn btn-danger waves-effect waves-light py-2 fs-13 shadow-sm" @click="openRequestModal(2)">
                                                        <i class="ri-git-repository-private-line me-2 align-bottom"></i> Enable Lost Mode
                                                    </button>
                                                </template>

                                                <!-- If status is LOST_MODE (3), show Disable Lost Mode -->
                                                <button v-if="deviceDetails.statusId === 3" class="btn btn-success waves-effect waves-light py-2 fs-13 shadow-sm" @click="openRequestModal(3)">
                                                    <i class="ri-lock-unlock-line me-2 align-bottom"></i> Disable Lost Mode
                                                </button>

                                                <!-- Fallback message if no results from status checks -->
                                                <div v-if="!deviceDetails.statusId" class="text-center text-muted fs-12 italic">
                                                    No management actions available for current state.
                                                </div>

                                                <div class="mt-4 pt-3 border-top w-100 text-center">
                                                    <p class="text-muted fs-12 mb-2">Track batch approvals or manage all devices in the central hub:</p>
                                                    <router-link to="/device/mdm-devices" class="btn btn-soft-primary btn-sm w-100">
                                                        <i class="ri-dashboard-2-line me-1 align-bottom"></i> Go to MDM Management Hub
                                                    </router-link>
                                                </div>
                                            </div>
                                        </div>
                                        <div v-else class="col-md-6 d-flex flex-column justify-content-center align-items-center">
                                            <div class="alert alert-soft-secondary text-center">
                                                <i class="ri-lock-2-line fs-24 mb-2 d-block"></i>
                                                <p class="mb-0">You do not have permission to request MDM commands.</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Request Approval Modal -->
        <div v-if="showModal" class="modal-backdrop fade show"></div>
        <div v-if="showModal" class="modal fade show d-block" tabindex="-1" role="dialog">
            <div class="modal-dialog modal-lg modal-dialog-centered" role="document">
                <div class="modal-content border-0 shadow">
                    <div class="modal-header bg-soft-primary p-3">
                        <h5 class="modal-title d-flex align-items-center">
                            <i class="ri-mail-send-line me-2"></i> Step 2: Select Message Template
                            <span class="badge badge-soft-primary ms-2 fs-10 text-uppercase">{{ modalTitle }}</span>
                        </h5>
                        <button type="button" class="btn-close" @click="showModal = false"></button>
                    </div>
                    <div class="modal-body p-4">
                        <div class="row" v-if="modalAction !== 3">
                            <div class="col-md-6">
                                <div class="mb-3">
                                    <label class="form-label text-muted fw-bold">Action Template <span class="text-danger">*</span></label>
                                    <div v-if="templatesLoading" class="py-2">
                                        <div class="spinner-border spinner-border-sm text-primary me-2"></div>
                                        <span class="text-muted">Loading templates...</span>
                                    </div>
                                    <select v-else class="form-select" v-model="selectedTemplateId">
                                        <option v-if="templates.length === 0" :value="null">No templates found</option>
                                        <option v-for="t in templates" :key="t.id" :value="t.id">{{ t.name || t.message_title }}</option>
                                    </select>
                                    <div v-if="!templatesLoading && templates.length === 0" class="mt-2 text-danger fs-11">
                                        <i class="ri-error-warning-line me-1"></i> Please create templates in MDM Settings for this action type.
                                    </div>
                                </div>
                            </div>
                            <div class="col-md-6">
                                <label class="form-label text-muted fw-bold">Message Preview</label>
                                <div class="preview-box p-3 border rounded bg-light-subtle">
                                    <div v-if="activeTemplate">
                                        <h6 class="fw-bold mb-2">{{ activeTemplate.name || activeTemplate.message_title }}</h6>
                                        <p class="mb-2 text-muted">{{ activeTemplate.message_body }}</p>
                                        <div class="border-top pt-2 mt-2" v-if="activeTemplate.footnote || activeTemplate.phone_number">
                                            <small class="text-muted d-block mb-1 italic" v-if="activeTemplate.footnote">"{{ activeTemplate.footnote }}"</small>
                                            <small class="fw-medium" v-if="activeTemplate.phone_number"><i class="ri-phone-line me-1"></i> {{ activeTemplate.phone_number }}</small>
                                        </div>
                                    </div>
                                    <div v-else class="text-muted text-center py-4">
                                        Select a template to see preview
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div v-else class="text-center py-4">
                            <div class="avatar-md mx-auto mb-3">
                                <div class="avatar-title bg-soft-success text-success fs-24 rounded-circle">
                                    <i class="ri-lock-unlock-line"></i>
                                </div>
                            </div>
                            <h5>Confirm Disable Lost Mode</h5>
                            <p class="text-muted">You are about to request approval to disable Lost Mode on this device. No message template is required for this action.</p>
                        </div>
                    </div>
                    <div class="modal-footer d-flex justify-content-between align-items-center bg-light-subtle rounded-bottom">
                        <div class="text-start pt-3">
                            <h6 class="mb-0 fw-bold">Ready for Approval Request!</h6>
                            <small class="text-muted">{{ modalAction === 3 ? 'Confirm this action to submit for approval.' : 'Selected template will be sent to the device after approval.' }}</small>
                        </div>
                        <button type="button" class="btn btn-primary px-4" @click="requestApproval" :disabled="(modalAction !== 3 && !selectedTemplateId) || isSubmitting">
                            <span v-if="isSubmitting" class="spinner-border spinner-border-sm me-1"></span>
                            <i v-else class="ri-mail-check-line me-1 align-bottom"></i> Request Approval
                        </button>
                    </div>
                </div>
            </div>
        </div>

    </Layout>
</template>

<style scoped>
.preview-box {
    min-height: 150px;
    font-size: 13px;
    line-height: 1.5;
}
.italic {
    font-style: italic;
}
.modal-title {
    color: #405189;
}
.bg-soft-primary {
    background-color: rgba(64, 81, 137, 0.1) !important;
}
.bg-light-subtle {
    background-color: #f3f3f9;
}
.badge-soft-info {
    background-color: rgba(41, 156, 219, 0.1);
    color: #299cdb;
}
</style>
